import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Notification Component
const Notification = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`notification ${type}`}>
      <span>{message}</span>
      <button onClick={onClose} className="notification-close">×</button>
    </div>
  );
};

// Notification Hook
const useNotification = () => {
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const hideNotification = () => {
    setNotification(null);
  };

  return { notification, showNotification, hideNotification };
};

// Body scroll lock — ref-counted, no layout shift, no jank.
// Multiple open modals share a single lock; restored only when the last unmounts.
let __scrollLockCount = 0;
let __savedBodyStyles = null;
const useBodyScrollLock = (active) => {
  useEffect(() => {
    if (!active) return undefined;
    if (__scrollLockCount === 0) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      __savedBodyStyles = {
        bodyOverflow: document.body.style.overflow,
        htmlOverflow: document.documentElement.style.overflow,
        paddingRight: document.body.style.paddingRight,
      };
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
    }
    __scrollLockCount += 1;
    return () => {
      __scrollLockCount -= 1;
      if (__scrollLockCount === 0 && __savedBodyStyles) {
        document.body.style.overflow = __savedBodyStyles.bodyOverflow;
        document.documentElement.style.overflow = __savedBodyStyles.htmlOverflow;
        document.body.style.paddingRight = __savedBodyStyles.paddingRight;
        __savedBodyStyles = null;
      }
    };
  }, [active]);
};

// Confirmation Modal Component
const ConfirmationModal = ({ isOpen, onConfirm, onCancel, message }) => {
  useBodyScrollLock(isOpen);
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay">
      <div className="confirmation-modal">
        <h3>Підтвердження</h3>
        <p>{message}</p>
        <div className="confirmation-buttons">
          <button className="confirm-btn" onClick={onConfirm}>Так</button>
          <button className="cancel-btn" onClick={onCancel}>Скасувати</button>
        </div>
      </div>
    </div>
  );
};

// Modal Component
const Modal = ({ isOpen, onClose, children }) => {
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        {children}
      </div>
    </div>
  );
};

// Contact Modal
const ContactModal = ({ isOpen, onClose, showNotification }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.phone || !formData.email || !formData.message) {
      showNotification('Заповніть всі поля', 'error');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      showNotification('Повідомлення відправлено успішно!', 'success');
      onClose();
      setFormData({ name: '', phone: '', email: '', message: '' });
    } catch (error) {
      showNotification('Помилка при відправці повідомлення', 'error');
    }
    setLoading(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="contact-modal">
        <h2 className="modal-title">Зв'яжіться з нами</h2>
        
        <div className="form-field">
          <label>Ваше ім'я</label>
          <input 
            placeholder="Як до вас звертатися?" 
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)} 
          />
        </div>

        <div className="form-field">
          <label>Номер телефону</label>
          <input 
            placeholder="+380 (__)___-__-__" 
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)} 
          />
        </div>

        <div className="form-field">
          <label>Електронна пошта</label>
          <input 
            type="email"
            placeholder="ваша@пошта.com" 
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)} 
          />
        </div>

        <div className="form-field">
          <label>Повідомлення</label>
          <textarea 
            placeholder="Напишіть ваше запитання або пропозицію..." 
            rows="4"
            value={formData.message}
            onChange={(e) => handleChange('message', e.target.value)} 
          />
        </div>

        <button 
          className="contact-submit-btn"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'ВІДПРАВЛЯЄМО...' : 'ВІДПРАВИТИ ПОВІДОМЛЕННЯ'}
        </button>
      </div>
    </Modal>
  );
};

// Ticket Purchase Modal
const TicketModal = ({ isOpen, onClose, showNotification }) => {
  const [step, setStep] = useState(1); // 1 = select tickets + email, 2 = payment instructions
  const [tickets, setTickets] = useState({
    first_day_tickets: 0,
    second_day_tickets: 0,
    two_day_tickets: 0
  });
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [paymentCode, setPaymentCode] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [paymentLinkClicked, setPaymentLinkClicked] = useState(false);

  const totalAmount = tickets.first_day_tickets * 300 + tickets.second_day_tickets * 300 + tickets.two_day_tickets * 500;

  const updateTicket = (type, change) => {
    setTickets(prev => ({
      ...prev,
      [type]: Math.max(0, prev[type] + change)
    }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleNextStep = async () => {
    if (totalAmount === 0) {
      showNotification('Будь ласка, оберіть хоча б один квиток', 'error');
      return;
    }
    
    if (!email) {
      showNotification('Будь ласка, вкажіть вашу електронну пошту', 'error');
      return;
    }
    
    if (!validateEmail(email)) {
      showNotification('Перевірте правильність введеної пошти', 'error');
      return;
    }

    setLoading(true);
    try {
      // Create the order and get payment code from backend
      const response = await axios.post(`${API}/tickets/purchase`, {
        email,
        ...tickets
      });
      
      // Use the payment code returned from backend
      setPaymentCode(response.data.payment_code);
      setPaymentLinkClicked(false); // Reset payment link click status
      setStep(2);
      
      // Scroll to top of modal after step change
      setTimeout(() => {
        const modalContent = document.querySelector('.modal-content');
        if (modalContent) {
          modalContent.scrollTop = 0;
        }
      }, 100);
    } catch (error) {
      console.error('Order creation error:', error);
      showNotification('Помилка при створенні замовлення: ' + (error.response?.data?.detail || 'Невідома помилка'), 'error');
    }
    setLoading(false);
  };

  const copyPaymentCode = () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(paymentCode);
        showNotification('Код оплати скопійовано!', 'success');
      } else {
        // Fallback for Telegram WebApp
        const textArea = document.createElement('textarea');
        textArea.value = paymentCode;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        showNotification('Код оплати скопійовано!', 'success');
      }
    } catch (error) {
      showNotification('Код: ' + paymentCode, 'error');
    }
  };

  const handlePaymentLinkClick = () => {
    setPaymentLinkClicked(true);
  };

  const handlePaymentConfirmation = async () => {
    if (!paymentLinkClicked) {
      showNotification('Спочатку натисніть кнопку "Оплатити" та здійсніть оплату', 'error');
      return;
    }

    setLoading(true);
    try {
      // Update status to awaiting_confirmation so admin knows user claims they paid
      await axios.put(`${API}/tickets/${paymentCode}/confirm-payment`);
      
      showNotification('Дякуємо за покупку! Після перевірки оплати квитки будуть надіслані на вашу пошту.', 'success');
      onClose();
      setStep(1);
      setTickets({ first_day_tickets: 0, second_day_tickets: 0, two_day_tickets: 0 });
      setEmail('');
      setPaymentCode('');
      setPaymentLinkClicked(false);
    } catch (error) {
      console.error('Error confirming payment:', error);
      showNotification('Помилка при оновленні статусу. Спробуйте ще раз.', 'error');
    }
    setLoading(false);
  };

  const handleCloseModal = () => {
    if (step === 2) {
      setShowConfirmation(true);
    } else {
      onClose();
      setStep(1);
      setTickets({ first_day_tickets: 0, second_day_tickets: 0, two_day_tickets: 0 });
      setEmail('');
      setPaymentCode('');
      setPaymentLinkClicked(false);
    }
  };

  const confirmClose = () => {
    setShowConfirmation(false);
    try {
      onClose();
      setStep(1);
      setTickets({ first_day_tickets: 0, second_day_tickets: 0, two_day_tickets: 0 });
      setEmail('');
      setPaymentCode('');
      setPaymentLinkClicked(false);
    } catch (error) {
      console.log('Close error:', error);
    }
  };

  const cancelClose = () => {
    try {
      setShowConfirmation(false);
    } catch (error) {
      console.log('Cancel error:', error);
    }
  };

  return (
    <>
      <ConfirmationModal 
        isOpen={showConfirmation}
        onConfirm={confirmClose}
        onCancel={cancelClose}
        message="Ви дійсно хочете закрити вікно?"
      />
      
      <Modal isOpen={isOpen} onClose={handleCloseModal}>
        <div className="ticket-modal">
          {step === 1 && (
            <>
              <h2 className="modal-title">Купити квитки</h2>
              
              <div className="ticket-option">
                <div className="ticket-info">
                  <span>Одноденний (Субота)</span>
                  <span className="price">300 грн</span>
                </div>
                <div className="ticket-controls">
                  <button onClick={() => updateTicket('first_day_tickets', -1)}>-</button>
                  <span>{tickets.first_day_tickets}</span>
                  <button onClick={() => updateTicket('first_day_tickets', 1)}>+</button>
                </div>
              </div>

              <div className="ticket-option">
                <div className="ticket-info">
                  <span>Одноденний (Неділя)</span>
                  <span className="price">300 грн</span>
                </div>
                <div className="ticket-controls">
                  <button onClick={() => updateTicket('second_day_tickets', -1)}>-</button>
                  <span>{tickets.second_day_tickets}</span>
                  <button onClick={() => updateTicket('second_day_tickets', 1)}>+</button>
                </div>
              </div>

              <div className="ticket-option special">
                <div className="ticket-info">
                  <span>Дводенний</span>
                  <span className="price">500 грн <span className="discount">Популярний!</span></span>
                </div>
                <div className="ticket-controls">
                  <button onClick={() => updateTicket('two_day_tickets', -1)}>-</button>
                  <span>{tickets.two_day_tickets}</span>
                  <button onClick={() => updateTicket('two_day_tickets', 1)}>+</button>
                </div>
              </div>

              <div className="free-notice">Діти до 7 років — безкоштовно у супроводі дорослого з квитком.<br /> 
                Учасники бойових дій — безкоштовно за наявності відповідного посвідчення.</div>

              <input
                type="email"
                placeholder="Ваша електронна пошта для отримання квитків"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`email-input ${email && !validateEmail(email) ? 'invalid-email' : ''}`}
              />

              <div className="total">Загалом: {totalAmount} грн</div>

              <button 
                className="purchase-btn"
                onClick={handleNextStep}
                disabled={totalAmount === 0 || !email || !validateEmail(email) || loading}
              >
                {loading ? 'СТВОРЕННЯ ЗАМОВЛЕННЯ...' : 'ПЕРЕЙТИ ДО ОПЛАТИ'}
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="modal-title">Інструкції з оплати</h2>
              
              <div className="payment-code-block">
                <h3>Ваш код оплати:</h3>
                <div className="payment-code-display clickable" onClick={copyPaymentCode} title="Натисніть, щоб скопіювати">
                  {paymentCode}
                </div>
                <p className="payment-code-note">Натисніть, щоб скопіювати.</p>
              </div>

              <div className="payment-instructions">
                <h3>Як здійснити оплату:</h3>
                <ol>
                  <li>Натисніть кнопку "Оплатити" для переходу на банку Monobank</li>
                  <li>Переведіть <strong>{totalAmount} грн</strong> на банку фестивалю</li>
                  <li>В коментарі до переказу обов'язково вкажіть код: <strong>{paymentCode}</strong></li>
                  <li>Після успішної оплати поверніться сюди і натисніть "Завершити замовлення"</li>
                  <li>Ми перевіримо вашу оплату і надішлемо квитки на email</li>
                </ol>
              </div>

              <a 
                href="https://send.monobank.ua/jar/9cnbPtKJ8e"
                target="_blank"
                rel="noopener noreferrer"
                className="monobank-link"
                onClick={handlePaymentLinkClick}
              >
                ОПЛАТИТИ
              </a>

              <button 
                className="payment-confirm-btn"
                onClick={handlePaymentConfirmation}
                disabled={!paymentLinkClicked}
              >
                ЗАВЕРШИТИ ЗАМОВЛЕННЯ
              </button>
              
              {!paymentLinkClicked && (
                <p style={{textAlign: 'center', fontSize: '0.9rem', opacity: 0.7, marginTop: '10px'}}>
                  Спочатку натисніть "Оплатити" та здійсніть оплату
                </p>
              )}
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

// Registration Modal
// Small helper: label above + placeholder example inside
const Field = ({ label, required, children }) => (
  <div className="form-field">
    <label>
      {label}
      {required ? <span className="field-required">*</span> : null}
    </label>
    {children}
  </div>
);

const RegistrationModal = ({ isOpen, onClose, type, showNotification }) => {
  const [formData, setFormData] = useState({});
  const [carPhotos, setCarPhotos] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [step, setStep] = useState(1);
  const fileInputRef = useRef(null);

  // Reset step & rules when modal closes/reopens or type changes
  useEffect(() => {
    setStep(1);
    setRulesAccepted(false);
  }, [isOpen, type]);

  // Cleanup blob preview URLs on unmount / change
  useEffect(() => {
    return () => {
      photoPreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []); // eslint-disable-line

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const goToStep2 = () => {
    if (!formData.full_name || !formData.email || !formData.phone || !formData.city) {
      showNotification('Будь ласка, заповніть всі обов\'язкові поля (позначені *)', 'error');
      return;
    }
    if (!validateEmail(formData.email)) {
      showNotification('Перевірте правильність введеної електронної пошти', 'error');
      return;
    }
    if (!validatePhone(formData.phone)) {
      showNotification('Перевірте правильність номеру телефону', 'error');
      return;
    }
    setStep(2);
  };

  const goToStep3 = () => {
    if (!formData.car_brand || !formData.car_model || carPhotos.length < 3) {
      showNotification('Будь ласка, заповніть всі обов\'язкові поля (позначені *) та додайте мінімум 3 фото', 'error');
      return;
    }
    setStep(3);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length < 3 || files.length > 5) {
      showNotification('Потрібно обрати від 3 до 5 фотографій', 'error');
      return;
    }

    // Check individual file sizes (warn if > 30MB)
    const oversizedFiles = files.filter(file => file.size > 30 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      showNotification(`Деякі фото дуже великі. Ми автоматично оптимізуємо їх для швидкого завантаження`, 'warning');
    }

    const photoPromises = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Resize if longer side exceeds 1920px
            const maxDimension = 1920;
            let { width, height } = img;
            
            // Only resize if any side is larger than 1920px
            if (Math.max(width, height) > maxDimension) {
              if (width > height) {
                height = height * (maxDimension / width);
                width = maxDimension;
              } else {
                width = width * (maxDimension / height);
                height = maxDimension;
              }
            }
            // If image is smaller than 1920px, keep original dimensions
            
            canvas.width = width;
            canvas.height = height;
            
            ctx.drawImage(img, 0, 0, width, height);
            // Maximum quality - no compression
            const compressedDataUrl = canvas.toDataURL('image/jpeg', 1.0);
            
            // Convert back to File object for multipart upload
            canvas.toBlob((blob) => {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              resolve(compressedFile);
            }, 'image/jpeg', 1.0);
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(photoPromises).then(photos => {
      // revoke old previews
      photoPreviews.forEach((url) => URL.revokeObjectURL(url));
      const previews = photos.map((f) => URL.createObjectURL(f));
      setCarPhotos(photos);
      setPhotoPreviews(previews);
    });
  };

  const removePhoto = (index) => {
    URL.revokeObjectURL(photoPreviews[index]);
    setCarPhotos((prev) => prev.filter((_, i) => i !== index));
    setPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const clearAllPhotos = () => {
    photoPreviews.forEach((url) => URL.revokeObjectURL(url));
    setCarPhotos([]);
    setPhotoPreviews([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Перевірка українського номера телефону (мінімум 10 цифр)
    const phoneRegex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const handleSubmit = async () => {
    // Validation
    if (type === 'participant' && (!formData.full_name || !formData.email || !formData.phone || !formData.city || !formData.car_brand || !formData.car_model || carPhotos.length < 3)) {
      showNotification('Будь ласка, заповніть всі обов\'язкові поля (позначені *) та додайте мінімум 3 фото', 'error');
      return;
    }

    if (type === 'participant' && !rulesAccepted) {
      showNotification('Щоб подати заявку, будь ласка, ознайомтесь з правилами фестивалю та підтвердіть згоду', 'error');
      return;
    }
    
    if (type !== 'participant' && (!formData.full_name || !formData.email || !formData.phone)) {
      showNotification('Будь ласка, заповніть всі обов\'язкові поля (позначені *)', 'error');
      return;
    }

    // Валідація email
    if (!validateEmail(formData.email)) {
      showNotification('Перевірте правильність введеної електронної пошти', 'error');
      return;
    }

    // Валідація телефону
    if (!validatePhone(formData.phone)) {
      showNotification('Перевірте правильність номеру телефону', 'error');
      return;
    }

    setLoading(true);
    try {
      let endpoint;
      
      switch (type) {
        case 'participant':
          endpoint = '/registration/participants';
          
          // Create FormData for multipart upload
          const formDataToSend = new FormData();
          
          // Add text fields
          formDataToSend.append('full_name', formData.full_name);
          formDataToSend.append('email', formData.email);
          formDataToSend.append('phone', formData.phone);
          formDataToSend.append('instagram', formData.instagram || '');
          formDataToSend.append('city', formData.city);
          formDataToSend.append('car_brand', formData.car_brand);
          formDataToSend.append('car_model', formData.car_model);
          formDataToSend.append('car_year', formData.car_year || '');
          formDataToSend.append('car_description', formData.car_description || '');
          
          // Add photo files
          carPhotos.forEach((photo, index) => {
            formDataToSend.append('car_photos', photo);
          });
          
          const response = await axios.post(`${API}${endpoint}`, formDataToSend, {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            timeout: 60000, // 60 second timeout for large uploads
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              // You could show progress here if needed
              console.log(`Upload Progress: ${percentCompleted}%`);
            }
          });
          
          showNotification(`Заявка відправлена! Номер реєстрації: ${response.data.registration_number}`, 'success');
          break;
          
        case 'media':
          endpoint = '/registration/media';
          let submitData = { 
            ...formData,
            instagram: formData.instagram || '',
            equipment: formData.equipment || '',
            additional_info: formData.additional_info || ''
          };
          
          const mediaResponse = await axios.post(`${API}${endpoint}`, submitData, {
            timeout: 30000
          });
          showNotification(`Заявка відправлена! Номер реєстрації: ${mediaResponse.data.registration_number}`, 'success');
          break;
          
        case 'partner':
          endpoint = '/registration/partners';
          submitData = { 
            ...formData,
            company_name: formData.company_name || '',
            additional_info: formData.additional_info || ''
          };
          
          const partnerResponse = await axios.post(`${API}${endpoint}`, submitData, {
            timeout: 30000
          });
          showNotification(`Заявка відправлена! Номер реєстрації: ${partnerResponse.data.registration_number}`, 'success');
          break;
          
        default:
          return;
      }
      
      onClose();
      setFormData({});
      photoPreviews.forEach((url) => URL.revokeObjectURL(url));
      setCarPhotos([]);
      setPhotoPreviews([]);
      setRulesAccepted(false);
    } catch (error) {
      console.error('Registration error:', error);
      if (error.code === 'ECONNABORTED') {
        showNotification('Час очікування вичерпано. Спробуйте ще раз або зменшіть розмір фото.', 'error');
      } else if (error.response?.status === 413) {
        showNotification('Файли занадто великі. Спробуйте зменшити кількість або розмір фото.', 'error');
      } else {
        showNotification('Помилка при відправці заявки: ' + (error.response?.data?.detail || 'Невідома помилка'), 'error');
      }
    }
    setLoading(false);
  };

  const participantSteps = [
    { num: 1, label: 'Інформація про учасника' },
    { num: 2, label: 'Інформація про авто' },
    { num: 3, label: 'Правила' },
  ];

  const renderStepIndicator = () => (
    <div className="step-pills" data-testid="form-step-indicator">
      {participantSteps.map((s) => {
        const isActive = step === s.num;
        const isDone = step > s.num;
        return (
          <button
            type="button"
            key={s.num}
            className={`step-pill ${isActive ? 'is-active' : ''} ${isDone ? 'is-done' : ''}`}
            onClick={() => {
              if (s.num < step) setStep(s.num);
            }}
            disabled={s.num > step}
            aria-current={isActive ? 'step' : undefined}
            data-testid={`step-pill-${s.num}`}
          >
            <span className="step-pill__dot" aria-hidden="true">{isDone ? '✓' : s.num}</span>
            <span className="step-pill__label">{s.label}</span>
          </button>
        );
      })}
    </div>
  );

  const renderForm = () => {
    switch (type) {
      case 'participant':
        if (step === 3) {
          return (
            <>
              <h2 className="modal-title">Реєстрація автомобіля</h2>
              {renderStepIndicator()}
              <div className="festival-rules festival-rules--page" data-testid="festival-rules-block">
                <div className="festival-rules__schedule">
                  <div className="festival-rules__schedule-title">Графік заїзду та виїзду</div>
                  <ul className="festival-rules__schedule-list">
                    <li><span>Заїзд</span><strong>Пʼятниця з 20:00 та субота до 10:00</strong></li>
                    <li><span>Виїзд</span><strong>Неділя з 20:00</strong></li>
                  </ul>
                  <div className="festival-rules__schedule-note">
                    В інші години заїзд та виїзд з території фестивалю неможливі.
                  </div>
                </div>

                <div className="festival-rules__forbidden">
                  <div className="festival-rules__forbidden-title">На території фестивалю категорично заборонено:</div>
                  <ul className="festival-rules__list">
                    <li>Прокручування коліс на місці (Burnout)</li>
                    <li>Демонстрація гучного вихлопу (розгазовка двигуна)</li>
                    <li>Керовані заноси автомобіля (Drift)</li>
                    <li>Вмикання гучної музики зі свого авто</li>
                    <li>Вживання ненормативної лексики</li>
                    <li>Куріння поза спеціально відведеними місцями</li>
                    <li>Демонстрація агресії щодо учасників та відвідувачів фестивалю</li>
                    <li>Вживання наркотичних, токсичних, психотропних та інших заборонених речовин</li>
                  </ul>
                </div>

                <div className="festival-rules__warning">
                  Ми створюємо фестиваль для всіх — у комфортній та безпечній атмосфері. У разі недотримання цих правил організатори залишають за собою право попросити учасника та його автомобіль залишити територію фестивалю.
                </div>

                <label className="festival-rules__agree" data-testid="rules-accept-label">
                  <input
                    type="checkbox"
                    checked={rulesAccepted}
                    onChange={(e) => setRulesAccepted(e.target.checked)}
                    data-testid="rules-accept-checkbox"
                  />
                  <span className="festival-rules__agree-box" aria-hidden="true"></span>
                  <span className="festival-rules__agree-text">
                    Я ознайомився з правилами фестивалю та зобовʼязуюсь їх дотримуватись
                  </span>
                </label>
              </div>
            </>
          );
        }
        if (step === 2) {
          return (
            <>
              <h2 className="modal-title">Реєстрація автомобіля</h2>
              {renderStepIndicator()}
              <div className="form-row">
                <Field label="Марка автомобіля" required>
                  <input
                    placeholder="Марка"
                    value={formData.car_brand || ''}
                    onChange={(e) => handleChange('car_brand', e.target.value)}
                  />
                </Field>
                <Field label="Модель автомобіля" required>
                  <input
                    placeholder="Модель"
                    value={formData.car_model || ''}
                    onChange={(e) => handleChange('car_model', e.target.value)}
                  />
                </Field>
              </div>
              <Field label="Рік випуску автомобіля" required>
                <input
                  placeholder="РРРР"
                  value={formData.car_year || ''}
                  onChange={(e) => handleChange('car_year', e.target.value)}
                />
              </Field>
              <Field label="Опис автомобіля та модифікацій" required>
                <textarea
                  placeholder="Опишіть особливості автомобіля, тюнінг, модифікації, історію"
                  value={formData.car_description || ''}
                  onChange={(e) => handleChange('car_description', e.target.value)}
                />
              </Field>
              <div className="photo-upload">
                <label>Фото автомобіля (3-5 фото) *</label>
                {carPhotos.length === 0 ? (
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileUpload}
                    className="file-input"
                    data-testid="car-photos-input"
                  />
                ) : (
                  <div className="photo-preview" data-testid="photo-preview">
                    <div className="photo-preview__grid">
                      {photoPreviews.map((src, idx) => (
                        <div className="photo-preview__item" key={src}>
                          <img src={src} alt={`Фото ${idx + 1}`} />
                          <button
                            type="button"
                            className="photo-preview__remove"
                            onClick={() => removePhoto(idx)}
                            aria-label={`Видалити фото ${idx + 1}`}
                            data-testid={`photo-remove-${idx}`}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="photo-preview__actions">
                      <span className="photo-preview__count">
                        Обрано {carPhotos.length} {carPhotos.length === 1 ? 'фото' : 'фото'}
                      </span>
                      <div className="photo-preview__buttons">
                        <button
                          type="button"
                          className="photo-preview__btn"
                          onClick={() => fileInputRef.current && fileInputRef.current.click()}
                          data-testid="photo-replace-btn"
                        >
                          Замінити фото
                        </button>
                        <button
                          type="button"
                          className="photo-preview__btn photo-preview__btn--ghost"
                          onClick={clearAllPhotos}
                          data-testid="photo-clear-btn"
                        >
                          Видалити всі
                        </button>
                      </div>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                    />
                  </div>
                )}
              </div>
            </>
          );
        }
        return (
          <>
            <h2 className="modal-title">Реєстрація автомобіля</h2>
            {renderStepIndicator()}
            <Field label="Ім'я та прізвище" required>
              <input
                placeholder="Введіть ваше ім'я та прізвище"
                value={formData.full_name || ''}
                onChange={(e) => handleChange('full_name', e.target.value)}
              />
            </Field>
            <Field label="Електронна пошта" required>
              <input
                type="email"
                placeholder="example@email.com"
                value={formData.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
                className={formData.email && !validateEmail(formData.email) ? 'invalid-input' : ''}
              />
            </Field>
            <Field label="Номер телефону" required>
              <input
                placeholder="+380 XX XXX XX XX"
                value={formData.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
                className={formData.phone && !validatePhone(formData.phone) ? 'invalid-input' : ''}
              />
            </Field>
            <Field label="Instagram">
              <input
                placeholder="@username"
                value={formData.instagram || ''}
                onChange={(e) => handleChange('instagram', e.target.value)}
              />
            </Field>
            <Field label="Ваше місто / населений пункт" required>
              <input
                placeholder="Місто, селище або село"
                value={formData.city || ''}
                onChange={(e) => handleChange('city', e.target.value)}
              />
            </Field>
          </>
        );
      case 'media':
        return (
          <>
            <h2 className="modal-title">Реєстрація медіа</h2>
            <Field label="Ім'я та прізвище" required>
              <input
                placeholder="Введіть ваше ім'я та прізвище"
                value={formData.full_name || ''}
                onChange={(e) => handleChange('full_name', e.target.value)}
              />
            </Field>
            <Field label="Електронна пошта" required>
              <input
                type="email"
                placeholder="example@email.com"
                value={formData.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
                className={formData.email && !validateEmail(formData.email) ? 'invalid-input' : ''}
              />
            </Field>
            <Field label="Номер телефону" required>
              <input
                placeholder="+380 XX XXX XX XX"
                value={formData.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
                className={formData.phone && !validatePhone(formData.phone) ? 'invalid-input' : ''}
              />
            </Field>
            <Field label="Instagram">
              <input
                placeholder="@username"
                onChange={(e) => handleChange('instagram', e.target.value)}
              />
            </Field>
            <Field label="Обладнання, яке використовуєте" required>
              <input
                placeholder="Камера, об'єктиви, освітлення тощо"
                onChange={(e) => handleChange('equipment', e.target.value)}
              />
            </Field>
            <Field label="Додаткова інформація">
              <textarea
                placeholder="Посилання на портфоліо, досвід зйомок, ваші роботи"
                onChange={(e) => handleChange('additional_info', e.target.value)}
              />
            </Field>
          </>
        );
      case 'partner':
        return (
          <>
            <h2 className="modal-title">Реєстрація партнера</h2>
            <Field label="Контактна особа" required>
              <input
                placeholder="Введіть ваше ім'я та прізвище"
                value={formData.full_name || ''}
                onChange={(e) => handleChange('full_name', e.target.value)}
              />
            </Field>
            <Field label="Електронна пошта" required>
              <input
                type="email"
                placeholder="example@email.com"
                value={formData.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
                className={formData.email && !validateEmail(formData.email) ? 'invalid-input' : ''}
              />
            </Field>
            <Field label="Номер телефону" required>
              <input
                placeholder="+380 XX XXX XX XX"
                value={formData.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
                className={formData.phone && !validatePhone(formData.phone) ? 'invalid-input' : ''}
              />
            </Field>
            <Field label="Назва компанії або бренду" required>
              <input
                placeholder="Назва компанії або бренду"
                onChange={(e) => handleChange('company_name', e.target.value)}
              />
            </Field>
            <Field label="Про вашу компанію">
              <textarea
                placeholder="Сфера діяльності, продукція або послуги, ідеї для співпраці"
                onChange={(e) => handleChange('additional_info', e.target.value)}
              />
            </Field>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="registration-modal">
        {renderForm()}
        {type === 'participant' && step === 1 ? (
          <button
            className="submit-btn"
            onClick={goToStep2}
            data-testid="registration-next-btn"
          >
            Далі — Інформація про авто →
          </button>
        ) : type === 'participant' && step === 2 ? (
          <div className="form-actions">
            <button
              type="button"
              className="back-btn"
              onClick={() => setStep(1)}
              disabled={loading}
              data-testid="registration-back-btn"
            >
              ← Назад
            </button>
            <button
              className="submit-btn submit-btn--inline"
              onClick={goToStep3}
              data-testid="registration-next-btn"
            >
              Далі — Правила →
            </button>
          </div>
        ) : type === 'participant' && step === 3 ? (
          <div className="form-actions">
            <button
              type="button"
              className="back-btn"
              onClick={() => setStep(2)}
              disabled={loading}
              data-testid="registration-back-btn"
            >
              ← Назад
            </button>
            <button
              className="submit-btn submit-btn--inline"
              onClick={handleSubmit}
              disabled={loading || !rulesAccepted}
              data-testid="registration-submit-btn"
            >
              {loading ? 'Відправка...' : 'Завершити реєстрацію'}
            </button>
          </div>
        ) : (
          <button
            className="submit-btn"
            onClick={handleSubmit}
            disabled={loading}
            data-testid="registration-submit-btn"
          >
            {loading ? 'Відправка...' : 'ВІДПРАВИТИ ЗАЯВКУ'}
          </button>
        )}
      </div>
    </Modal>
  );
};

// Ticket Scanner Component
const TicketScanner = ({ showNotification }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState(''); // success, error, warning, empty
  const [zoomLevel, setZoomLevel] = useState(1); // 1x or 2x zoom
  const [manualCode, setManualCode] = useState(''); // For manual barcode input
  const [currentDay, setCurrentDay] = useState(1); // 1 for first day, 2 for second day
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const codeReader = useRef(null);
  const statusTimeoutRef = useRef(null);

  useEffect(() => {
    // Initialize ZXing code reader
    import('@zxing/library').then(({ BrowserMultiFormatReader }) => {
      codeReader.current = new BrowserMultiFormatReader();
    });

    return () => {
      stopScanning();
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
    };
  }, []);

  const showStatus = (message, type = 'success') => {
    setStatusMessage(message);
    setStatusType(type);
    
    // Clear previous timeout
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
    }
    
    // Clear status after 10 seconds
    statusTimeoutRef.current = setTimeout(() => {
      setStatusMessage('');
      setStatusType('empty');
    }, 10000);
  };

  const toggleZoom = () => {
    const newZoom = zoomLevel === 1 ? 2 : 1;
    setZoomLevel(newZoom);
    
    // If camera is active, restart with new zoom
    if (isScanning && videoRef.current && videoRef.current.srcObject) {
      restartCameraWithZoom(newZoom);
    }
  };

  const restartCameraWithZoom = async (zoom) => {
    try {
      // Stop current stream
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject;
        const tracks = stream.getTracks();
        tracks.forEach(track => track.stop());
      }

      // Start new stream with zoom
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 * zoom },
          height: { ideal: 720 * zoom }
        }
      });
      
      videoRef.current.srcObject = stream;
      
      // Restart scanning
      codeReader.current.decodeFromVideoDevice(undefined, videoRef.current, (result, error) => {
        if (result) {
          handleScanResult(result.getText());
        }
      });
      
    } catch (error) {
      console.error('Zoom restart error:', error);
    }
  };

  const startScanning = async () => {
    try {
      setIsScanning(true);
      setStatusMessage('');
      setStatusType('empty');
      
      if (!codeReader.current) {
        showNotification('Сканер не готовий. Спробуйте пізніше.', 'error');
        return;
      }

      // Get camera stream with zoom
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 * zoomLevel },
          height: { ideal: 720 * zoomLevel }
        }
      });
      
      videoRef.current.srcObject = stream;
      
      // Start scanning
      codeReader.current.decodeFromVideoDevice(undefined, videoRef.current, (result, error) => {
        if (result) {
          handleScanResult(result.getText());
        }
      });
      
    } catch (error) {
      console.error('Camera access error:', error);
      showNotification('Помилка доступу до камери. Перевірте дозволи.', 'error');
      setIsScanning(false);
    }
  };

  const stopScanning = () => {
    if (codeReader.current) {
      codeReader.current.reset();
    }
    
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    
    setIsScanning(false);
  };

  const handleScanResult = async (barcode) => {
    // Instead of immediately processing, just put the barcode in the input field
    setManualCode(barcode);
    showStatus(`📷 Код відскановано: ${barcode} - натисніть "Перевірити"`, 'warning');
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!manualCode.trim() || loading) return;
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/scan-ticket`, { 
        barcode: manualCode.trim(),
        day: currentDay
      });
      const data = response.data;
      
      if (data.success) {
        const ticketInfo = data.ticket_info || {};
        showStatus(`${data.message} (${ticketInfo.ticket_type || 'Тип невідомий'})`, 'success');
      } else {
        if (data.status === 'used') {
          showStatus(`${data.message}`, 'warning');
        } else {
          // All other cases (invalid, unknown, unpaid, etc.) = invalid
          showStatus(`${data.message}`, 'error');
        }
      }
      
      // Clear the input after checking
      setManualCode('');
      
    } catch (error) {
      console.error('Scan error:', error);
      showStatus(`❌ Помилка сканування`, 'error');
    }
    
    setLoading(false);
  };

  return (
    <div className="ticket-scanner">
      <h2>Сканування білетів</h2>
      
      <div className="scanner-controls">
        <div className="day-selector">
          <label>День фестивалю:</label>
          <select 
            value={currentDay} 
            onChange={(e) => setCurrentDay(parseInt(e.target.value))}
            className="day-select"
          >
            <option value={1}>Субота</option>
            <option value={2}>Неділя</option>
          </select>
        </div>
        
        <div className="scanner-main-controls">
          {!isScanning ? (
            <button 
              className="scanner-start-btn"
              onClick={startScanning}
            >
              Відкрити камеру
            </button>
          ) : (
            <button 
              className="scanner-stop-btn"
              onClick={stopScanning}
            >
              Закрити камеру
            </button>
          )}
        </div>
        
        {isScanning && (
          <div className="scanner-zoom-controls">
            <button 
              className="scanner-zoom-btn"
              onClick={toggleZoom}
              title={`Переключити на ${zoomLevel === 1 ? '2x' : '1x'} збільшення`}
            >
              {zoomLevel === 1 ? '🔍 2x' : '🔍 1x'}
            </button>
          </div>
        )}
      </div>
      
      {isScanning && (
        <div className="scanner-container">
          <video 
            ref={videoRef}
            className="scanner-video"
            autoPlay
            playsInline
            muted
            style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'center' }}
          />
          <canvas 
            ref={canvasRef}
            className="scanner-canvas"
            style={{ display: 'none' }}
          />
          <div className="scanner-overlay">
            <div className="scanner-frame"></div>
            <p className="scanner-instruction">
              Наведіть камеру на штрих-код {zoomLevel === 2 && '(збільшено 2x)'}
            </p>
          </div>
        </div>
      )}
      
      {/* Manual Input */}
      <div className="manual-input-container">
        <h3>Введіть штрих-код</h3>
        <form onSubmit={handleManualSubmit} className="manual-input-form">
          <input
            type="text"
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            placeholder="Введіть штрих-код або відскануйте камерою"
            className="manual-input-field"
          />
          <button 
            type="submit" 
            className="manual-submit-btn"
            disabled={!manualCode.trim() || loading}
          >
            {loading ? 'Перевірка...' : 'Перевірити'}
          </button>
        </form>
      </div>
      
      {/* Fixed Status Bar */}
      <div className={`scanner-status-bar ${statusType}`}>
        {statusMessage || 'Очікування сканування...'}
      </div>
      
      {loading && (
        <div className="scanner-loading">
          Перевірка білета...
        </div>
      )}
    </div>
  );
};

// Admin Login Component
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) {
      setError('Введіть пароль');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        sessionStorage.setItem('adminLoggedIn', 'true');
        onLogin();
      }
    } catch (error) {
      setError('Невірний пароль');
    }
    setLoading(false);
  };

  return (
    <div className="admin-login">
      <div className="admin-login-card">
        <h2>Адміністративна панель</h2>
        <p>Для доступу введіть пароль:</p>
        
        <form onSubmit={handleLogin}>
          <input
            type="password"
            placeholder="Пароль"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="admin-password-input"
          />
          
          {error && <div className="admin-login-error">{error}</div>}
          
          <button 
            type="submit" 
            className="admin-login-btn"
            disabled={loading}
          >
            {loading ? 'Перевірка...' : 'Увійти'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Admin Component
const Admin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [data, setData] = useState({
    participants: [],
    media: [],
    partners: [],
    tickets: [],
    contacts: [],
    stats: {},
    settings: {}
  });
  const [activeFilter, setActiveFilter] = useState({
    participants: 'all',
    media: 'all', 
    partners: 'all',
    tickets: 'all'
  });
  
  // Add notification functionality
  const { notification, showNotification, hideNotification } = useNotification();

  useEffect(() => {
    // Check if admin is already logged in
    const adminLoggedIn = sessionStorage.getItem('adminLoggedIn');
    if (adminLoggedIn === 'true') {
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetchData();
    }
  }, [isLoggedIn]);

  const handleLogin = () => {
    setIsLoggedIn(true);
    fetchData();
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminLoggedIn');
    setIsLoggedIn(false);
    setData({
      participants: [],
      media: [],
      partners: [],
      tickets: [],
      contacts: [],
      stats: {},
      settings: {}
    });
  };

  const clearDatabase = async () => {
    if (window.confirm('УВАГА! Це видалить ВСІ дані (заявки, квитки, контакти). Продовжити?')) {
      try {
        await axios.delete(`${API}/admin/clear-database`);
        showNotification('База даних очищена!', 'success');
        fetchData(); // Refresh data
      } catch (error) {
        showNotification('Помилка при очищенні бази даних', 'error');
      }
    }
  };

  if (!isLoggedIn) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  const fetchData = async () => {
    try {
      const [participants, media, partners, tickets, contacts, stats, settings] = await Promise.all([
        axios.get(`${API}/admin/participants`),
        axios.get(`${API}/admin/media`),
        axios.get(`${API}/admin/partners`),
        axios.get(`${API}/admin/tickets`),
        axios.get(`${API}/admin/contacts`),
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/settings`)
      ]);

      setData({
        participants: participants.data,
        media: media.data,
        partners: partners.data,
        tickets: tickets.data,
        contacts: contacts.data,
        stats: stats.data,
        settings: settings.data
      });
    } catch (error) {
      console.error('Error fetching admin data:', error);
    }
  };

  const updateStatus = async (type, id, status) => {
    try {
      await axios.put(`${API}/admin/${type}/${id}`, { status });
      fetchData();
      if (type === 'partners') {
        showNotification('Статус партнера оновлено', 'success');
      } else {
        showNotification('Статус оновлено та повідомлення надіслано', 'success');
      }
    } catch (error) {
      showNotification('Помилка при оновленні статусу', 'error');
    }
  };

  const updateSettings = async (settingName, value) => {
    try {
      await axios.put(`${API}/admin/settings`, { [settingName]: value });
      fetchData();
    } catch (error) {
      showNotification('Помилка при оновленні налаштувань', 'error');
    }
  };



  const confirmPayment = async (ticketId) => {
    try {
      await axios.put(`${API}/admin/tickets/${ticketId}/confirm`);
      fetchData();
      showNotification('Оплату підтверджено, квитки надіслані!', 'success');
    } catch (error) {
      showNotification('Помилка при підтвердженні оплати', 'error');
    }
  };

  const rejectPayment = async (ticketId) => {
    try {
      await axios.put(`${API}/admin/tickets/${ticketId}/reject`);
      fetchData();
      showNotification('Оплату відхилено, повідомлення надіслано', 'success');
    } catch (error) {
      showNotification('Помилка при відхиленні оплати', 'error');
    }
  };

  const exportParticipantsExcel = async () => {
    try {
      const response = await axios.get(`${API}/admin/participants/export`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const ts = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const filename = `zdybanka_participants_${ts.getFullYear()}${pad(ts.getMonth() + 1)}${pad(ts.getDate())}_${pad(ts.getHours())}${pad(ts.getMinutes())}${pad(ts.getSeconds())}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      showNotification('Excel-файл успішно завантажено!', 'success');
    } catch (error) {
      console.error('Export error:', error);
      showNotification('Помилка при експорті в Excel', 'error');
    }
  };

  const getFilteredData = (dataType) => {
    const currentData = data[dataType] || [];
    const currentFilter = activeFilter[dataType];
    
    if (!Array.isArray(currentData)) {
      return [];
    }
    
    if (currentFilter === 'all') {
      return currentData;
    }
    
    return currentData.filter(item => item.status === currentFilter);
  };

  const getStatusCount = (dataType, status) => {
    const currentData = data[dataType] || [];
    
    if (!Array.isArray(currentData)) {
      return 0;
    }
    
    if (status === 'all') {
      return currentData.length;
    }
    return currentData.filter(item => item.status === status).length;
  };

  const renderStatusFilters = (dataType) => {
    const statusOptions = dataType === 'tickets' 
      ? [
          { key: 'all', label: 'Всі', color: '#6c757d' },
          { key: 'pending', label: 'Очікують оплати', color: '#6c757d' },
          { key: 'awaiting_confirmation', label: 'Очікують підтвердження', color: '#ffc107' },
          { key: 'confirmed', label: 'Підтверджені', color: '#28a745' },
          { key: 'rejected', label: 'Відхилені', color: '#dc3545' }
        ]
      : [
          { key: 'all', label: 'Всі', color: '#6c757d' },
          { key: 'pending', label: 'Очікують', color: '#ffc107' },
          { key: 'approved', label: 'Схвалені', color: '#28a745' },
          { key: 'rejected', label: 'Відхилені', color: '#dc3545' }
        ];

    return (
      <div className="status-filters">
        {statusOptions.map(option => (
          <button
            key={option.key}
            className={`status-filter-btn ${activeFilter[dataType] === option.key ? 'active' : ''}`}
            onClick={() => setActiveFilter(prev => ({...prev, [dataType]: option.key}))}
            style={{ 
              backgroundColor: activeFilter[dataType] === option.key ? option.color : 'transparent',
              color: activeFilter[dataType] === option.key ? 'white' : option.color,
              borderColor: option.color
            }}
          >
            {option.label} ({getStatusCount(dataType, option.key)})
          </button>
        ))}
      </div>
    );
  };

  const renderTable = () => {
    const filteredData = getFilteredData(activeTab);
    
    if (activeTab === 'scanner') {
      return <TicketScanner showNotification={showNotification} />;
    }

    if (activeTab === 'settings') {
      return (
        <div className="settings-panel">
          <h2>Управління реєстраціями та продажами</h2>
          
          <div className="settings-grid">
            <div className="setting-item">
              <div className="setting-info">
                <h3>Реєстрація учасників</h3>
                <p>Дозволити подавати заявки на участь в фестивалі</p>
              </div>
              <label className="toggle-switch">
                <input 
                  type="checkbox" 
                  checked={data.settings.participant_registration_enabled || false}
                  onChange={(e) => updateSettings('participant_registration_enabled', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <h3>Реєстрація медіа</h3>
                <p>Дозволити подавати заявки для медіа акредитації</p>
              </div>
              <label className="toggle-switch">
                <input 
                  type="checkbox" 
                  checked={data.settings.media_registration_enabled || false}
                  onChange={(e) => updateSettings('media_registration_enabled', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <h3>Реєстрація партнерів</h3>
                <p>Дозволити подавати заявки для партнерства</p>
              </div>
              <label className="toggle-switch">
                <input 
                  type="checkbox" 
                  checked={data.settings.partner_registration_enabled || false}
                  onChange={(e) => updateSettings('partner_registration_enabled', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <h3>Продаж квитків</h3>
                <p>Дозволити покупку квитків на фестиваль</p>
              </div>
              <label className="toggle-switch">
                <input 
                  type="checkbox" 
                  checked={data.settings.ticket_sales_enabled || false}
                  onChange={(e) => updateSettings('ticket_sales_enabled', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>
      );
    }


    
    if (activeTab === 'stats') {
      return (
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Учасники</h3>
            <p>Всього: {data.stats.participants?.total || 0}</p>
            <p>Очікують: {data.stats.participants?.pending || 0}</p>
            <p>Схвалено: {data.stats.participants?.approved || 0}</p>
            <p>Відхилено: {data.stats.participants?.rejected || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Медіа</h3>
            <p>Всього: {data.stats.media?.total || 0}</p>
            <p>Очікують: {data.stats.media?.pending || 0}</p>
            <p>Схвалено: {data.stats.media?.approved || 0}</p>
            <p>Відхилено: {data.stats.media?.rejected || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Партнери</h3>
            <p>Всього: {data.stats.partners?.total || 0}</p>
            <p>Очікують: {data.stats.partners?.pending || 0}</p>
            <p>Схвалено: {data.stats.partners?.approved || 0}</p>
            <p>Відхилено: {data.stats.partners?.rejected || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Квитки</h3>
            <p>Продано: {data.stats.tickets?.total_sales || 0}</p>
            <p>Дохід: {data.stats.tickets?.total_revenue || 0} грн</p>
          </div>
        </div>
      );
    }

    if (activeTab === 'contacts') {
      const contactsData = data.contacts || [];
      return (
        <div className="contacts-list">
          {contactsData.map(contact => (
            <div key={contact.id} className="contact-card">
              <div className="contact-header">
                <h3>{contact.name}</h3>
                <span className={`status-badge ${contact.status}`}>
                  {contact.status === 'new' ? 'Новий' : 
                   contact.status === 'read' ? 'Прочитано' : 'Відповідь надіслана'}
                </span>
              </div>
              <div className="contact-details">
                <p><strong>Email:</strong> {contact.email}</p>
                <p><strong>Телефон:</strong> {contact.phone}</p>
                <p><strong>Дата:</strong> {new Date(contact.timestamp).toLocaleDateString()}</p>
              </div>
              <div className="contact-message">
                <strong>Повідомлення:</strong>
                <p>{contact.message}</p>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (activeTab === 'tickets') {
      return (
        <div>
          {renderStatusFilters('tickets')}
          <div className="tickets-list">
            {filteredData.map(ticket => (
            <div key={ticket.id} className="ticket-card">
              <div className="ticket-header">
                <div className="ticket-basic-info">
                  <h3>{ticket.email}</h3>
                  <span className={`status-badge ${ticket.status}`}>
                    {ticket.status === 'pending' ? 'Очікує оплати' :
                     ticket.status === 'awaiting_confirmation' ? 'Очікує підтвердження' :
                     ticket.status === 'confirmed' ? 'Оплачено' : 'Відхилено'}
                  </span>
                </div>
                <div className="payment-code-admin">
                  <strong>Код оплати:</strong>
                  <div className="payment-code-display-admin">
                    {ticket.payment_code || 'N/A'}
                  </div>
                </div>
              </div>
              
              <div className="ticket-details">
                <div className="ticket-info-grid">
                  <div><strong>Одноденний (Субота):</strong> {ticket.first_day_tickets} шт.</div>
                  <div><strong>Одноденний (Неділя):</strong> {ticket.second_day_tickets} шт.</div>
                  <div><strong>Дводенний:</strong> {ticket.two_day_tickets} шт.</div>
                  <div><strong>Загальна сума:</strong> {ticket.total_amount} грн</div>
                  <div><strong>Дата:</strong> {new Date(ticket.timestamp).toLocaleDateString()}</div>
                </div>
                
                {(ticket.status === 'pending' || ticket.status === 'awaiting_confirmation') && (
                  <div className="ticket-actions">
                    <button 
                      className="confirm-payment-btn"
                      onClick={() => confirmPayment(ticket.id)}
                    >
                      Підтвердити оплату
                    </button>
                    <button 
                      className="reject-payment-btn"
                      onClick={() => rejectPayment(ticket.id)}
                    >
                      Відхилити оплату
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          </div>
        </div>
      );
    }

    return (
      <div>
        {activeTab === 'participants' ? (
          <div className="filters-with-export" data-testid="participants-export-bar">
            {renderStatusFilters(activeTab)}
            <button
              type="button"
              className="export-excel-btn"
              data-testid="export-participants-excel-btn"
              onClick={exportParticipantsExcel}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              Експортувати в Excel
            </button>
          </div>
        ) : (
          renderStatusFilters(activeTab)
        )}
        <div className="registrations-list">
          {filteredData.map(item => (
          <div key={item.id} className="registration-card">
            <div className="registration-header">
              <div className="reg-basic-info">
                <h3>{item.registration_number} - {item.full_name}</h3>
                <span className={`status-badge ${item.status}`}>
                  {item.status === 'pending' ? 'Очікує' : 
                   item.status === 'approved' ? 'Схвалено' : 'Відхилено'}
                </span>
              </div>
              {item.status === 'pending' && (
                <div className="reg-actions">
                  <button 
                    className="approve-btn"
                    onClick={() => updateStatus(activeTab, item.id, 'approved')}
                  >
                    Схвалити
                  </button>
                  <button 
                    className="reject-btn"
                    onClick={() => updateStatus(activeTab, item.id, 'rejected')}
                  >
                    Відхилити
                  </button>
                </div>
              )}
            </div>
            
            <div className="registration-details">
              <div className="reg-info-grid">
                <div><strong>Email:</strong> {item.email}</div>
                <div><strong>Телефон:</strong> {item.phone}</div>
                <div><strong>Instagram:</strong> {item.instagram || '-'}</div>
                
                {activeTab === 'participants' && (
                  <>
                    <div><strong>Місто:</strong> {item.city || '-'}</div>
                    <div><strong>Марка:</strong> {item.car_brand}</div>
                    <div><strong>Модель:</strong> {item.car_model}</div>
                    <div><strong>Рік:</strong> {item.car_year}</div>
                  </>
                )}
                
                {activeTab === 'media' && (
                  <div><strong>Обладнання:</strong> {item.equipment}</div>
                )}
                
                {activeTab === 'partners' && (
                  <div><strong>Компанія:</strong> {item.company_name}</div>
                )}
              </div>
              
              {activeTab === 'participants' && item.car_description && (
                <div className="reg-description">
                  <strong>Опис автомобіля:</strong>
                  <p>{item.car_description}</p>
                </div>
              )}
              
              {(activeTab === 'media' || activeTab === 'partners') && item.additional_info && (
                <div className="reg-description">
                  <strong>Додаткова інформація:</strong>
                  <p>{item.additional_info}</p>
                </div>
              )}
              
              {activeTab === 'participants' && item.car_photos && item.car_photos.length > 0 && (
                <div className="reg-photos">
                  <strong>Фото автомобіля:</strong>
                  <div className="photo-gallery-large">
                    {item.car_photos.map((photo, index) => (
                      <img 
                        key={index}
                        src={photo} 
                        alt={`Car ${index + 1}`}
                        className="car-photo-large"
                        onClick={() => window.open(photo, '_blank')}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        </div>
      </div>
    );
  };

  return (
    <div className="admin-container">
      {/* Notification */}
      {notification && (
        <Notification 
          message={notification.message} 
          type={notification.type} 
          onClose={hideNotification} 
        />
      )}
      
      <div className="admin-header">
        <h1>Адмін панель ЗДИБАНКА</h1>
        <div className="admin-header-buttons">
          <button className="admin-clear-btn" onClick={clearDatabase}>
            Очистити БД
          </button>
          <button className="admin-logout-btn" onClick={handleLogout}>
            Вийти
          </button>
        </div>
      </div>
      
      <div className="admin-tabs">
        <button 
          className={activeTab === 'settings' ? 'active' : ''}
          onClick={() => setActiveTab('settings')}
        >
          Налаштування
        </button>
        <button 
          className={activeTab === 'participants' ? 'active' : ''}
          onClick={() => setActiveTab('participants')}
        >
          Учасники ({data.participants.length})
        </button>
        <button 
          className={activeTab === 'media' ? 'active' : ''}
          onClick={() => setActiveTab('media')}
        >
          Медіа ({data.media.length})
        </button>
        <button 
          className={activeTab === 'partners' ? 'active' : ''}
          onClick={() => setActiveTab('partners')}
        >
          Партнери ({data.partners.length})
        </button>
        <button 
          className={activeTab === 'contacts' ? 'active' : ''}
          onClick={() => setActiveTab('contacts')}
        >
          Контакти ({data.contacts.length})
        </button>
        <button 
          className={activeTab === 'tickets' ? 'active' : ''}
          onClick={() => setActiveTab('tickets')}
        >
          Квитки ({data.tickets.length})
        </button>
        <button 
          className={activeTab === 'stats' ? 'active' : ''}
          onClick={() => setActiveTab('stats')}
        >
          Статистика
        </button>
        <button 
          className={activeTab === 'scanner' ? 'active' : ''}
          onClick={() => setActiveTab('scanner')}
        >
          Сканування
        </button>
      </div>

      <div className="admin-content">
        {renderTable()}
      </div>
    </div>
  );
};

// Home Component
const Home = () => {
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [registrationType, setRegistrationType] = useState('');
  const [settings, setSettings] = useState({});
  const [photos, setPhotos] = useState([]);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [videoOverlayOpacity, setVideoOverlayOpacity] = useState(0);
  const carouselContainerRef = useRef(null);
  const carouselTrackRef = useRef(null);
  const carouselPositionRef = useRef(0);
  const carouselWidthRef = useRef(0);
  const carouselMovingRef = useRef(false);
  const carouselResumeTimerRef = useRef(null);
  const carouselHoveredRef = useRef(false);
  const { notification, showNotification, hideNotification } = useNotification();

  // Video darkening effect on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      // Start darkening after hero section, full dark at 2x window height
      const opacity = Math.min(scrollY / (windowHeight * 1), 0.95);
      setVideoOverlayOpacity(opacity);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchPhotos();
  }, []);

  // Measure a complete set, including gaps, after image loads and resizing.
  useLayoutEffect(() => {
    const container = carouselContainerRef.current;
    const track = carouselTrackRef.current;
    if (!photos.length || !container || !track) return undefined;

    carouselWidthRef.current = 0;
    carouselPositionRef.current = 0;
    const updatePosition = () => {
      const firstPhoto = track.children[0];
      const nextSet = track.children[photos.length];
      if (!firstPhoto || !nextSet) return;
      const width = nextSet.offsetLeft - firstPhoto.offsetLeft;
      if (width <= 0) return;

      const previousWidth = carouselWidthRef.current;
      const previousOffset = previousWidth
        ? ((carouselPositionRef.current - previousWidth) % previousWidth + previousWidth) % previousWidth
        : 0;
      carouselWidthRef.current = width;
      carouselPositionRef.current = width + (previousOffset % width);
      carouselMovingRef.current = false;
      track.style.transition = 'none';
      track.style.transform = `translateX(-${carouselPositionRef.current}px)`;
      // Commit the jump before restoring the CSS transition.
      track.getBoundingClientRect();
      track.style.transition = '';
    };

    updatePosition();
    const observer = new ResizeObserver(updatePosition);
    observer.observe(container);
    Array.from(track.children).slice(0, photos.length).forEach(photo => observer.observe(photo));
    return () => observer.disconnect();
  }, [photos]);

  const moveCarousel = useCallback((direction) => {
    const container = carouselContainerRef.current;
    const track = carouselTrackRef.current;
    const width = carouselWidthRef.current;
    if (!container || !track || !width || carouselMovingRef.current) return;

    const step = Math.min(container.clientWidth, width);
    if (!step) return;
    carouselMovingRef.current = true;
    carouselPositionRef.current += direction * step;
    track.style.transform = `translateX(-${carouselPositionRef.current}px)`;
  }, []);

  const nextPhotos = useCallback(() => moveCarousel(1), [moveCarousel]);
  const prevPhotos = useCallback(() => moveCarousel(-1), [moveCarousel]);

  const handleCarouselTransitionEnd = (event) => {
    const track = carouselTrackRef.current;
    const width = carouselWidthRef.current;
    if (event.target !== track || event.propertyName !== 'transform' || !width) return;

    const position = carouselPositionRef.current;
    const wrapped = width + ((position - width) % width + width) % width;
    if (Math.abs(position - wrapped) > 0.01) {
      track.style.transition = 'none';
      track.style.transform = `translateX(-${wrapped}px)`;
      track.getBoundingClientRect();
      track.style.transition = '';
    }
    carouselPositionRef.current = wrapped;
    carouselMovingRef.current = false;
  };

  // Auto-scroll every 4 seconds.
  useEffect(() => {
    if (!photos.length || !isAutoScrolling) return undefined;
    const interval = setInterval(nextPhotos, 4000);
    return () => clearInterval(interval);
  }, [photos.length, isAutoScrolling, nextPhotos]);

  useEffect(() => () => clearTimeout(carouselResumeTimerRef.current), []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/status`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API}/gallery/photos`);
      setPhotos(response.data.photos || []);
    } catch (error) {
      console.error('Error fetching photos:', error);
    }
  };

  // Pause after manual navigation; keep paused while the pointer is over photos.
  const pauseAutoScroll = () => {
    setIsAutoScrolling(false);
    clearTimeout(carouselResumeTimerRef.current);
    carouselResumeTimerRef.current = setTimeout(() => {
      if (!carouselHoveredRef.current) setIsAutoScrolling(true);
    }, 5000);
  };

  const handleNext = () => {
    pauseAutoScroll();
    nextPhotos();
  };

  const handlePrev = () => {
    pauseAutoScroll();
    prevPhotos();
  };

  const handleMouseEnter = () => {
    carouselHoveredRef.current = true;
    clearTimeout(carouselResumeTimerRef.current);
    setIsAutoScrolling(false);
  };

  const handleMouseLeave = () => {
    carouselHoveredRef.current = false;
    setIsAutoScrolling(true);
  };

  const openTicketModal = () => {
    if (!settings.ticket_sales_enabled) {
      showNotification('Продаж квитків тимчасово зупинено', 'error');
      return;
    }
    setTicketModalOpen(true);
  };

  const openRegistration = (type) => {
    let enabled = false;
    let message = '';
    
    switch (type) {
      case 'participant':
        enabled = settings.participant_registration_enabled;
        message = 'Реєстрація учасників тимчасово зупинена';
        break;
      case 'media':
        enabled = settings.media_registration_enabled;
        message = 'Реєстрація медіа тимчасово зупинена';
        break;
      case 'partner':
        enabled = settings.partner_registration_enabled;
        message = 'Реєстрація партнерів тимчасово зупинена';
        break;
    }
    
    if (!enabled) {
      showNotification(message, 'error');
      return;
    }
    
    setRegistrationType(type);
    setRegistrationModalOpen(true);
  };

  // Get photos for infinite display
  const getDisplayPhotos = () => {
    if (photos.length === 0) return [];
    // Triple the array for seamless infinite scroll
    return [...photos, ...photos, ...photos];
  };

  return (
    <div className="home-container">
      {/* Notification */}
      {notification && (
        <Notification 
          message={notification.message} 
          type={notification.type} 
          onClose={hideNotification} 
        />
      )}

      {/* Contact Button */}
      <button 
        className="contact-fab"
        onClick={() => setContactModalOpen(true)}
        title="Зв'язатися з нами"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
        </svg>
      </button>

      {/* Hero Section */}
      <section className="hero">
        <video autoPlay muted loop playsInline className="hero-video">
          <source src="/background_video.mp4" type="video/mp4" />
        </video>
        <div 
          className="video-overlay-scroll" 
          style={{ opacity: videoOverlayOpacity }}
        ></div>
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <img src="/zdybanka-logo.png" alt="ЗДИБАНКА" className="festival-logo" />
          <p className="festival-subtitle">АВТОМОБІЛЬНИЙ ФЕСТИВАЛЬ</p>
          <p className="festival-date">8-9 ТРАВНЯ 2027 • ТРЦ VICTORIA GARDENS, ЛЬВІВ</p>
          
          <div className="hero-buttons">
            <button 
              className={`btn btn-primary ${!settings.ticket_sales_enabled ? 'disabled' : ''}`}
              onClick={openTicketModal}
            >
              КУПИТИ КВИТОК
            </button>
            <button 
              className={`btn btn-secondary ${!settings.participant_registration_enabled ? 'disabled' : ''}`}
              onClick={() => openRegistration('participant')}
            >
              ЗАРЕЄСТРУВАТИСЯ
            </button>
          </div>
        </div>
        <div className="hero-transition"></div>
      </section>

      {/* About Festival Section */}
      <section className="about">
        <div className="container">
          <h2>ПРО ФЕСТИВАЛЬ</h2>
          <p>
            ЗДИБАНКА — це найяскравіший автомобільний фестиваль, що об’єднує справжніх шанувальників стильних авто, 
            тюнінгу та автокультури. Два дні емоцій, виставка унікальних машин, живе спілкування з однодумцями та атмосфера, 
            в якій відчувається справжня любов до автомобілів.
          </p>
          
          {/* Photo Carousel */}
          <div className="about-carousel">
            <div 
              className="carousel-container"
              ref={carouselContainerRef}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <div
                className="carousel-track"
                ref={carouselTrackRef}
                onTransitionEnd={handleCarouselTransitionEnd}
              >
                {getDisplayPhotos().map((photo, index) => (
                  <div key={index} className="carousel-photo">
                    <img src={photo} alt={`Здибанка — фото ${(index % photos.length) + 1}`} decoding="async" />
                  </div>
                ))}
              </div>
              <button className="carousel-btn prev" onClick={handlePrev} aria-label="Попередні фото">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z"/>
                </svg>
              </button>
              <button className="carousel-btn next" onClick={handleNext} aria-label="Наступні фото">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Participation Section */}
      <section className="participation">
        <div className="container">
          <h2>СТАТИ УЧАСНИКОМ</h2>
          <p>Оберіть свій варіант участі у фестивалі</p>
          
          <div className="participation-cards">
            <div className="participation-card participant">
              <h3>ДЛЯ АВТОВЛАСНИКІВ</h3>
              <p>Покажіть свій унікальний автомобіль на фестивалі у Львові</p>
              <button 
                className={`btn ${!settings.participant_registration_enabled ? 'disabled' : ''}`}
                onClick={() => openRegistration('participant')}
              >
                ЗАРЕЄСТРУВАТИСЯ
              </button>
            </div>
            
            <div className="participation-card media">
              <h3>ДЛЯ МЕДІА</h3>
              <p>Акредитація для журналістів, фотографів та блогерів</p>
              <button 
                className={`btn ${!settings.media_registration_enabled ? 'disabled' : ''}`}
                onClick={() => openRegistration('media')}
              >
                ЗАРЕЄСТРУВАТИСЯ
              </button>
            </div>
            
            <div className="participation-card partner">
              <h3>ДЛЯ ПАРТНЕРІВ</h3>
              <p>Стратегічне партнерство та спонсорські можливості</p>
              <button 
                className={`btn ${!settings.partner_registration_enabled ? 'disabled' : ''}`}
                onClick={() => openRegistration('partner')}
              >
                ЗАРЕЄСТРУВАТИСЯ
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="location">
        <div className="container">
          <h2>ЛОКАЦІЯ</h2>
          <div className="location-content">
            <div className="location-main">
              <div className="location-header">
                <div className="location-details">
                  <h3>ТРЦ Victoria Gardens</h3>
                  <p className="location-address">3-й рівень критого паркінгу</p>
                  <p className="location-address">вул. Кульпарківська, 226 А, Львів</p>
                </div>
              </div>
              

              <div className="location-instructions">
                <h4>Як нас знайти?</h4>
                <div className="instruction-steps-horizontal">
                  <div className="instruction-step-compact">
                    <div className="step-number">1</div>
                    <span>Зайдіть у ТРЦ Victoria Gardens</span>
                  </div>
                  
                  <div className="step-arrow">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/>
                    </svg>
                  </div>
                  
                  <div className="instruction-step-compact">
                    <div className="step-number">2</div>
                    <span>Підніміться на другий поверх</span>
                  </div>
                  
                  <div className="step-arrow">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/>
                    </svg>
                  </div>
                  
                  <div className="instruction-step-compact">
                    <div className="step-number">3</div>
                    <span>Знайдіть перехід на 3-й рівень паркінгу</span>
                  </div>
                  
                  <div className="step-arrow">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/>
                    </svg>
                  </div>
                  
                  <div className="instruction-step-compact">
                    <div className="step-number">4</div>
                    <span>Ласкаво просимо на ЗДИБАНКА!</span>
                  </div>
                </div>
              </div>
              
              <div className="location-cta">
                <a 
                  href="https://maps.app.goo.gl/agCLdRG7vqKySBLb6" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="location-btn"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                  Відкрити на мапі
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contacts Section */}
      <section className="contacts">
        <div className="container">
          <h2>КОНТАКТИ</h2>
          
          {/* Contact Details */}
          <div className="contact-details-section">
            <div className="contact-detail-item">
              <div className="contact-detail-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                </svg>
              </div>
              <a href="tel:+380939640500" className="contact-detail-value">+38 (093) 964-05-00</a>
            </div>
            
            <div className="contact-detail-item">
              <div className="contact-detail-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.89 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
              </div>
              <a href="mailto:zdybankafest@gmail.com" className="contact-detail-value">zdybankafest@gmail.com</a>
            </div>
          </div>

          {/* Social Media */}
          <div className="contact-icon-group">
            <a href="https://instagram.com/zdybankafest" target="_blank" rel="noopener noreferrer" className="contact-icon-item instagram">
              <div className="contact-icon-circle">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </div>
              <span className="contact-label">Instagram</span>
            </a>
            <a href="https://t.me/zdybankafest" target="_blank" rel="noopener noreferrer" className="contact-icon-item telegram">
              <div className="contact-icon-circle">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
              </div>
              <span className="contact-label">Telegram</span>
            </a>
            <a href="https://youtube.com/@zdybankafest" target="_blank" rel="noopener noreferrer" className="contact-icon-item youtube">
              <div className="contact-icon-circle">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </div>
              <span className="contact-label">YouTube</span>
            </a>
          </div>
        </div>
      </section>

      {/* Admin Panel */}
      {window.location.pathname === '/admin' && <Admin />}

      {/* Notification */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
          <button onClick={hideNotification}>×</button>
        </div>
      )}

      {/* Modals */}
      <TicketModal 
        isOpen={ticketModalOpen} 
        onClose={() => setTicketModalOpen(false)} 
        showNotification={showNotification}
      />
      
      <RegistrationModal 
        isOpen={registrationModalOpen} 
        onClose={() => setRegistrationModalOpen(false)}
        type={registrationType}
        showNotification={showNotification}
      />

      <ContactModal 
        isOpen={contactModalOpen} 
        onClose={() => setContactModalOpen(false)} 
        showNotification={showNotification}
      />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
